import { TakiConfigError } from "./errors.js";
import type {
  Branding,
  BrandingColors,
  EmulatorEndpoint,
  EmulatorsConfig,
  FirebaseConfig,
  TakiConfig,
} from "./types.js";

/** Emplacement public servi a la racine du site (Firebase Hosting). */
const CONFIG_URL = "/taki.config.json";

/**
 * Config d'exemple versionnee dans le repo, servie elle aussi a la racine.
 * Filet de secours en DEV uniquement : cloner le repo puis `pnpm dev` marche
 * sans copie manuelle prealable.
 */
const EXAMPLE_CONFIG_URL = "/taki.config.example.json";

/** Resultat d'une tentative de recuperation d'un fichier de config. */
type FetchOutcome =
  | { found: true; raw: unknown }
  | { found: false; reason: string };

/**
 * Recupere et parse un fichier de config JSON.
 *
 * Distingue deux cas : fichier introuvable/injoignable (`found: false`, le
 * flux appelant peut tenter un fallback) et fichier present mais illisible
 * (leve : un fichier casse ne doit jamais etre masque par un fallback).
 */
async function fetchConfigJson(url: string): Promise<FetchOutcome> {
  let response: Response;
  try {
    response = await fetch(url, {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
  } catch (cause) {
    return {
      found: false,
      reason: `impossible de recuperer ${url} (erreur reseau : ${
        cause instanceof Error ? cause.message : String(cause)
      })`,
    };
  }

  if (!response.ok) {
    // 404 = cas "fichier absent" explicitement demande par le contrat.
    return {
      found: false,
      reason: `echec du chargement de ${url} : ${response.status} ${response.statusText}`,
    };
  }

  try {
    return { found: true, raw: await response.json() };
  } catch (cause) {
    throw new TakiConfigError(`Le contenu de ${url} n'est pas un JSON valide.`, {
      cause,
    });
  }
}

/**
 * Charge la configuration client au RUNTIME depuis `/taki.config.json`.
 *
 * Flux : `fetch` -> parse JSON -> validation structurelle -> `TakiConfig` type.
 * Doit etre appele AVANT `initializeApp` (cf. `@taki/sdk`).
 *
 * Si `/taki.config.json` est absent (404), on retombe sur
 * `/taki.config.example.json` avec un avertissement console explicite : le
 * depot fraichement clone demarre sans etape manuelle. Un `taki.config.json`
 * PRESENT mais casse leve, il n'est jamais masque par le fallback.
 *
 * Aucune dependance Firebase : ce package ne fait que recuperer et valider la
 * forme de la config.
 *
 * @throws {TakiConfigError} si aucun des deux fichiers n'est joignable, si la
 *   reponse n'est pas un JSON valide, ou si la structure ne respecte pas le
 *   contrat.
 */
export async function loadTakiConfig(): Promise<TakiConfig> {
  const primary = await fetchConfigJson(CONFIG_URL);
  if (primary.found) {
    return parseTakiConfig(primary.raw);
  }

  console.warn(
    `[@taki/runtime-config] config locale absente, fallback sur ${EXAMPLE_CONFIG_URL} (${primary.reason}). ` +
      `Cree ${CONFIG_URL} pour une config dediee ; en dev local, "pnpm dev:local" le fait pour toi.`,
  );

  const fallback = await fetchConfigJson(EXAMPLE_CONFIG_URL);
  if (fallback.found) {
    return parseTakiConfig(fallback.raw);
  }

  throw new TakiConfigError(
    `Aucune configuration trouvee : ${primary.reason} ; ${fallback.reason}. ` +
      `Le fichier ${CONFIG_URL} doit etre deploye a la racine du site.`,
  );
}

/* -------------------------------------------------------------------------- */
/* Validation structurelle (sans dependance externe)                          */
/* -------------------------------------------------------------------------- */

const FIREBASE_KEYS = [
  "apiKey",
  "authDomain",
  "projectId",
  "storageBucket",
  "messagingSenderId",
  "appId",
] as const satisfies ReadonlyArray<keyof FirebaseConfig>;

const COLOR_KEYS = [
  "primary",
  "background",
  "foreground",
] as const satisfies ReadonlyArray<keyof BrandingColors>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function requireString(
  source: Record<string, unknown>,
  key: string,
  path: string,
): string {
  const value = source[key];
  if (typeof value !== "string" || value.length === 0) {
    throw new TakiConfigError(
      `Champ "${path}" manquant ou invalide dans taki.config.json (chaine non vide attendue).`,
    );
  }
  return value;
}

function parseFirebaseConfig(raw: unknown): FirebaseConfig {
  if (!isRecord(raw)) {
    throw new TakiConfigError(
      `Champ "firebaseConfig" manquant ou invalide dans taki.config.json (objet attendu).`,
    );
  }
  const result = {} as FirebaseConfig;
  for (const key of FIREBASE_KEYS) {
    result[key] = requireString(raw, key, `firebaseConfig.${key}`);
  }
  return result;
}

function parseBranding(raw: unknown): Branding {
  if (!isRecord(raw)) {
    throw new TakiConfigError(
      `Champ "branding" manquant ou invalide dans taki.config.json (objet attendu).`,
    );
  }

  const colorsRaw = raw["colors"];
  if (!isRecord(colorsRaw)) {
    throw new TakiConfigError(
      `Champ "branding.colors" manquant ou invalide dans taki.config.json (objet attendu).`,
    );
  }
  const colors = {} as BrandingColors;
  for (const key of COLOR_KEYS) {
    colors[key] = requireString(colorsRaw, key, `branding.colors.${key}`);
  }

  const branding: Branding = {
    name: requireString(raw, "name", "branding.name"),
    colors,
  };

  const logoUrl = raw["logoUrl"];
  if (logoUrl !== undefined) {
    if (typeof logoUrl !== "string") {
      throw new TakiConfigError(
        `Champ "branding.logoUrl" invalide dans taki.config.json (chaine attendue).`,
      );
    }
    branding.logoUrl = logoUrl;
  }

  return branding;
}

function parseInstalledApps(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    throw new TakiConfigError(
      `Champ "installedApps" manquant ou invalide dans taki.config.json (tableau attendu).`,
    );
  }
  return raw.map((entry, index) => {
    if (typeof entry !== "string" || entry.length === 0) {
      throw new TakiConfigError(
        `Element "installedApps[${index}]" invalide dans taki.config.json (chaine non vide attendue).`,
      );
    }
    return entry;
  });
}

function parseFlags(raw: unknown): Record<string, boolean> {
  if (raw === undefined) {
    return {};
  }
  if (!isRecord(raw)) {
    throw new TakiConfigError(
      `Champ "flags" invalide dans taki.config.json (objet attendu).`,
    );
  }
  const flags: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value !== "boolean") {
      throw new TakiConfigError(
        `Champ "flags.${key}" invalide dans taki.config.json (booleen attendu).`,
      );
    }
    flags[key] = value;
  }
  return flags;
}

function parseEmulatorEndpoint(raw: unknown, path: string): EmulatorEndpoint {
  if (!isRecord(raw)) {
    throw new TakiConfigError(
      `Champ "${path}" manquant ou invalide dans taki.config.json (objet { host, port } attendu).`,
    );
  }
  const port = raw["port"];
  if (typeof port !== "number" || !Number.isInteger(port) || port <= 0) {
    throw new TakiConfigError(
      `Champ "${path}.port" manquant ou invalide dans taki.config.json (entier positif attendu).`,
    );
  }
  return { host: requireString(raw, "host", `${path}.host`), port };
}

/**
 * Bloc `emulators` OPTIONNEL : present uniquement en dev local. S'il est la,
 * les deux emulateurs (firestore et auth) sont exiges : un branchement partiel
 * (Firestore local + Auth cloud) n'a aucun sens et masquerait un oubli.
 */
function parseEmulators(raw: unknown): EmulatorsConfig | undefined {
  if (raw === undefined) {
    return undefined;
  }
  if (!isRecord(raw)) {
    throw new TakiConfigError(
      `Champ "emulators" invalide dans taki.config.json (objet attendu).`,
    );
  }
  return {
    firestore: parseEmulatorEndpoint(raw["firestore"], "emulators.firestore"),
    auth: parseEmulatorEndpoint(raw["auth"], "emulators.auth"),
  };
}

/**
 * Valide une valeur inconnue (issue du JSON) et la renvoie typee `TakiConfig`.
 * Exportee pour permettre de valider une config injectee/mockee sans `fetch`
 * (tests, SSR de preview, etc.).
 *
 * @throws {TakiConfigError} si la structure ne respecte pas le contrat.
 */
export function parseTakiConfig(raw: unknown): TakiConfig {
  if (!isRecord(raw)) {
    throw new TakiConfigError(
      `taki.config.json doit contenir un objet JSON a la racine.`,
    );
  }

  const config: TakiConfig = {
    firebaseConfig: parseFirebaseConfig(raw["firebaseConfig"]),
    orgId: requireString(raw, "orgId", "orgId"),
    installedApps: parseInstalledApps(raw["installedApps"]),
    branding: parseBranding(raw["branding"]),
    flags: parseFlags(raw["flags"]),
  };

  const emulators = parseEmulators(raw["emulators"]);
  if (emulators !== undefined) {
    config.emulators = emulators;
  }

  return config;
}
